/**
 * `resolvePacing` (D153) — whether a clock runs right now, and what it counts
 * toward.
 *
 * @remarks
 * A pure derived read, the `resolveRoomAmbience` precedent exactly: nothing
 * is written, the transcript never learns a clock existed, and a re-render
 * re-derives the same answer from the same state. The engine's clock is
 * turns; this resolver never reads a wall clock and never computes seconds.
 * It answers two questions the SHELL asks — at what rate should silence cost
 * a `wait`, and how many turns remain to the named deadline — and the shell
 * does the multiplication.
 *
 * `undefined` means no clock runs: the pack declares none, the walk landed
 * on `'none'`, or the story is over (a terminal ending has closed the world,
 * and a metronome ticking `wait` at a closed door would only stack refusals).
 * A countdown whose event has no pending occurrence resolves WITHOUT a
 * countdown rather than inventing a number — the pace still runs. A
 * countdown that reads a counter (D244) always has a number: the amount.
 *
 * @packageDocumentation
 */

import type { ConditionalPace, EventDefinitionId, PaceValue, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { firstMatchingVariant } from '../conditions/first-matching-variant.js';
import { counterValueOf } from '../entities/counter-state.js';
import { pendingOccurrencesOf } from '../execution/pending-occurrences.js';
import { terminalEndingOf } from './session-ended.js';

/** A running clock, with the countdown's turns remaining filled in when there is one. */
export interface ResolvedPacing {
	/** Milliseconds of silence that cost one `wait`. */
	readonly autoWaitMs: number;
	readonly countdown?: {
		/** The deadline counted toward — absent when the countdown reads a counter (D244). */
		readonly eventId?: EventDefinitionId;
		readonly label: string;
		/** Turns until the named occurrence fires — the turn that fires it included — or (D244) the counter's value. */
		readonly turnsRemaining: number;
	};
}

/** Resolve one pace declaration against the live world — the one walk (D158), not a fifth restatement of it. */
const choosePaceValue = (declaration: ConditionalPace, bundle: EngineGameBundle, state: RuntimeState): PaceValue =>
	typeof declaration === 'string' || !('variants' in declaration)
		? declaration
		: (firstMatchingVariant(declaration.variants, (variant) => variant.condition, bundle, state)?.pace ?? declaration.fallback);

/**
 * Turns until the named event's soonest pending occurrence fires, or
 * `undefined` when nothing is pending — a clock with nothing to count toward.
 */
const turnsRemainingTo = (state: RuntimeState, eventId: EventDefinitionId): number | undefined => {
	const dueTurns = pendingOccurrencesOf(state, eventId).map((pending) => pending.dueTurn);

	// The tick fires an occurrence due at or before `turnCount + 1`, so the
	// distance is measured from the current count and never reads below one.
	return dueTurns.length === 0 ? undefined : Math.max(1, Math.min(...dueTurns) - state.progression.turnCount);
};

/**
 * Resolve the pack's pacing against the live world.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The running clock, or `undefined` when none runs
 */
export const resolvePacing = (bundle: EngineGameBundle, state: RuntimeState): ResolvedPacing | undefined => {
	const { pacing } = bundle.contentPack;

	if (pacing === undefined || terminalEndingOf(bundle, state) !== undefined) {
		return undefined;
	}

	const pace = choosePaceValue(pacing.pace, bundle, state);

	if (pace === 'none') {
		return undefined;
	}

	const { countdown } = pace;

	if (countdown === undefined) {
		return { autoWaitMs: pace.autoWaitMs };
	}

	// D244: a countdown that reads a COUNTER shows its value — the suit's
	// minutes are the turns left — and never reads below zero on screen.
	if ('counterId' in countdown) {
		return { autoWaitMs: pace.autoWaitMs, countdown: { label: countdown.label, turnsRemaining: Math.max(0, counterValueOf(bundle, state, countdown.counterId)) }};
	}

	const turnsRemaining = turnsRemainingTo(state, countdown.eventId);

	return turnsRemaining === undefined ? { autoWaitMs: pace.autoWaitMs } : { autoWaitMs: pace.autoWaitMs, countdown: { ...countdown, turnsRemaining }};
};
